import React, { ChangeEvent, ReactNode } from 'react';
import { Switch } from 'evergreen-ui';
import { SettingRow } from './SettingRow';
import styles from '../TargetGroupSettings.module.css';

interface SubSwitch {
    label: string;
    checked: boolean;
    onChange: (checked: boolean) => void;
    disabled?: boolean;
}

interface MultiSwitchRowProps {
    label: ReactNode;
    description?: ReactNode;
    switches: SubSwitch[];
}

/**
 * Row variant that groups multiple related switches under one title in a
 * single SettingRow. Used in MotiviewTab for:
 *   - "Show candidate details form" (before / after, mutually exclusive)
 *   - "Allow meetings" (above / average / below average, independent)
 *
 * The right column stacks each sub-switch as `[label] [Switch]` so the user
 * sees one labelled group instead of N separate rows in the table. Mutually
 * exclusive rules (e.g. the details-form mutex) live in the caller's onChange
 * callbacks — this component does no state management of its own.
 */
export const MultiSwitchRow = ({
    label,
    description,
    switches,
}: MultiSwitchRowProps) => (
    <SettingRow label={label} description={description}>
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
            }}>
            {switches.map((sw, index) => (
                <div
                    key={index}
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 12,
                    }}>
                    <span style={{ fontSize: '0.9em', color: '#445870' }}>
                        {sw.label}
                    </span>
                    <Switch
                        height={24}
                        checked={sw.checked}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            sw.onChange(event.target.checked)
                        }
                        disabled={sw.disabled}
                        className={styles.switch}
                    />
                </div>
            ))}
        </div>
    </SettingRow>
);

export default MultiSwitchRow;
